// src/pages/purchases.jsx
import React, { useState, useEffect } from 'react';
import { Music, Download, Calendar, AlertCircle, ShoppingBag } from 'lucide-react';
import { usePurchases } from '../components/music/hooks/usePurchases';

export default function PurchasesPage({ currentUser, showToast }) {
  const purchases = usePurchases(currentUser);
  const [localPurchases, setLocalPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  useEffect(() => {
    if (purchases) {
      setLocalPurchases(purchases);
      setLoading(false);
    }
  }, [purchases]);

  const handleDownload = async (track) => {
    if (currentUser && purchases && typeof purchases.isPurchased === 'function') {
      if (purchases.isPurchased(track.id)) {
        setIsSendingEmail(true);
        try {
          const sendEmailUrl = `${process.env.REACT_APP_SEND_MUSIC_EMAIL || 'https://us-central1-asidorsmusic.cloudfunctions.net/sendMusicViaEmail'}`;
          
          const response = await fetch(sendEmailUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              userId: currentUser.uid,
              musicId: track.id,
              userEmail: currentUser.email
            })
          });

          const data = await response.json();

          if (!response.ok || data.error) {
            showToast(`Failed to send music: ${data.error || 'Unknown error'}`, 'error');
            setIsSendingEmail(false);
            return;
          }

          showToast(`✅ Music file sent to ${currentUser.email}!`, 'success');
          setIsSendingEmail(false);
        } catch (error) {
          console.error('Error sending music email:', error);
          showToast('Failed to send music. Please try again.', 'error');
          setIsSendingEmail(false);
        }
      }
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 flex items-start space-x-4">
            <AlertCircle className="text-yellow-600 mt-1 flex-shrink-0" size={24} />
            <div>
              <h2 className="text-lg font-semibold text-yellow-900">Sign In Required</h2>
              <p className="text-yellow-800 mt-1">Please sign in to view your purchases.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">My Purchases</h1>
          <p className="text-gray-600">Your purchased music and exclusive content</p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : localPurchases && localPurchases.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {localPurchases.map((purchase) => (
              <div 
                key={purchase.id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition overflow-hidden"
              >
                {purchase.coverUrl && (
                  <img 
                    src={purchase.coverUrl} 
                    alt={purchase.title}
                    className="w-full h-48 object-cover"
                  />
                )}
                
                <div className="p-4">
                  <div className="flex items-start space-x-2 mb-2">
                    <Music size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{purchase.title}</h3>
                      <p className="text-sm text-gray-600">{purchase.artist}</p>
                    </div>
                  </div>

                  {purchase.purchaseDate && (
                    <div className="flex items-center space-x-2 text-sm text-gray-500 mb-3">
                      <Calendar size={16} />
                      <span>{new Date(purchase.purchaseDate).toLocaleDateString()}</span>
                    </div>
                  )}

                  <button
                    onClick={() => handleDownload(purchase)}
                    disabled={isSendingEmail}
                    className="w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-2 rounded-lg transition font-medium text-sm"
                  >
                    <Download size={18} />
                    <span>{isSendingEmail ? 'Sending...' : 'Download'}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <ShoppingBag size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Purchases Yet</h3>
            <p className="text-gray-600 mb-6">You haven't purchased any music yet. Check out our music store!</p>
            <a
              href="/music"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition"
            >
              Browse Music
            </a>
          </div>
        )}
      </div>
    </div>
  );
}